/**
 * Communication Log Component (T077)
 * Timeline view with type icons, direction indicators, and add form
 */

import { useState } from 'react';
import {
  MessageSquare,
  Mail,
  Phone,
  Send,
  Clock,
  Plus,
  X,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import {
  useCustomerCommunications,
  useCreateCommunication,
  type CommunicationLog as CommunicationLogType,
} from '../../hooks/useCustomers';

interface CommunicationLogProps {
  customerId: string;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const typeIcons: Record<string, React.ReactNode> = {
  email: <Mail className="w-4 h-4" />,
  phone: <Phone className="w-4 h-4" />,
  sms: <MessageSquare className="w-4 h-4" />,
  note: <MessageSquare className="w-4 h-4" />,
  other: <MessageSquare className="w-4 h-4" />,
};

const typeLabels: Record<string, string> = {
  email: 'Email',
  phone: 'Phone Call',
  sms: 'SMS',
  note: 'Note',
  other: 'Other',
};

interface AddCommunicationFormProps {
  customerId: string;
  onClose: () => void;
}

function AddCommunicationForm({ customerId, onClose }: AddCommunicationFormProps) {
  const [type, setType] = useState('email');
  const [direction, setDirection] = useState('outbound');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const createCommunication = useCreateCommunication();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createCommunication.mutate(
      {
        customerId,
        data: {
          type,
          direction,
          subject: subject || undefined,
          body: body || undefined,
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <div className="claude-card p-6 border-primary/30">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Log Communication</h3>
        <button onClick={onClose} className="claude-button claude-button-ghost p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="claude-input">
              <option value="email">Email</option>
              <option value="phone">Phone Call</option>
              <option value="sms">SMS</option>
              <option value="note">Note</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Direction</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="claude-input"
            >
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description..."
            className="claude-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Details</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Communication details..."
            rows={4}
            className="claude-input min-h-[100px] resize-y"
          />
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="claude-button claude-button-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createCommunication.isPending}
            className="claude-button claude-button-primary text-sm"
          >
            {createCommunication.isPending ? (
              <>
                <div className="claude-spinner w-4 h-4" />
                Saving...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function TimelineItem({ entry }: { entry: CommunicationLogType }) {
  const [expanded, setExpanded] = useState(false);
  const icon = typeIcons[entry.type] || typeIcons.other;
  const typeLabel = typeLabels[entry.type] || entry.type;
  const isInbound = entry.direction === 'inbound';

  return (
    <div className="flex gap-4">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isInbound ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'
          }`}
        >
          {icon}
        </div>
        <div className="w-px flex-1 bg-border mt-2" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-6 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-sm">{typeLabel}</span>
          <span
            className={`inline-flex items-center gap-1 text-xs ${
              isInbound ? 'text-primary' : 'text-success'
            }`}
          >
            {isInbound ? (
              <>
                <ArrowDownLeft className="w-3 h-3" /> Inbound
              </>
            ) : (
              <>
                <ArrowUpRight className="w-3 h-3" /> Outbound
              </>
            )}
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {formatDate(entry.createdAt)} at {formatTime(entry.createdAt)}
          </span>
        </div>

        {entry.subject && <p className="text-sm font-medium mb-1">{entry.subject}</p>}

        {entry.body && (
          <>
            <div
              className={`text-sm text-muted-foreground whitespace-pre-wrap ${
                !expanded && entry.body.length > 200 ? 'line-clamp-3' : ''
              }`}
            >
              {entry.body}
            </div>
            {entry.body.length > 200 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-primary hover:underline mt-1"
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}

        {entry.createdBy && (
          <p className="text-xs text-muted-foreground mt-1">by {entry.createdBy}</p>
        )}
      </div>
    </div>
  );
}

export default function CommunicationLog({ customerId }: CommunicationLogProps) {
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading } = useCustomerCommunications(customerId, page);

  const entries = data?.items || [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading communications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Communication Button / Form */}
      {showForm ? (
        <AddCommunicationForm customerId={customerId} onClose={() => setShowForm(false)} />
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="claude-button claude-button-primary text-sm"
          >
            <Plus className="w-4 h-4" />
            Log Communication
          </button>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="claude-card">
          <div className="claude-empty-state">
            <MessageSquare className="claude-empty-state-icon" />
            <h3 className="claude-empty-state-title">No communications logged</h3>
            <p className="claude-empty-state-description">
              Start logging communications to keep track of interactions with this customer.
            </p>
          </div>
        </div>
      ) : (
        <div className="claude-card p-6">
          {entries.map((entry) => (
            <TimelineItem key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="claude-button claude-button-secondary text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.pages}
              className="claude-button claude-button-secondary text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
